import { useState, useEffect, useCallback, useRef } from "react"
import api from "../services/api"
import { apiPath, appPath } from "../config/paths"
import { isAdminIpDenied } from "../utils/adminIpError"

export function useAuth({ onSessionReplaced } = {}) {
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem("user")) } catch { return null }
    })
    const [isAuthLoading, setIsAuthLoading] = useState(true)

    // Use a ref so the event listener closure always sees the latest callback
    // without needing to be re-registered on every render.
    const onSessionReplacedRef = useRef(onSessionReplaced)
    useEffect(() => { onSessionReplacedRef.current = onSessionReplaced }, [onSessionReplaced])

    const fetchUserData = useCallback(async () => {
        try {
            const res = await api.get("/auth/me")
            const next = res.data
            setUser((prev) => {
                if (prev && next && JSON.stringify(prev) === JSON.stringify(next)) return prev
                localStorage.setItem("user", JSON.stringify(next))
                return next
            })
            return next
        } catch (err) {
            if (err.response?.status === 401 || isAdminIpDenied(err)) {
                setUser(null)
                localStorage.removeItem("user")
                if (isAdminIpDenied(err)) {
                    try { await api.post("/auth/logout") } catch (_) {}
                }
            }
            return null
        }
    }, [])

    useEffect(() => {
        const path = window.location.pathname
        const loginPath = appPath("/login").replace(/\/$/, "")
        if (path === loginPath || path.startsWith(`${loginPath}/`)) {
            setIsAuthLoading(false)
            return
        }
        fetchUserData().finally(() => setIsAuthLoading(false))
    }, [fetchUserData])

    useEffect(() => {
        const clearSession = () => {
            setUser(null)
            localStorage.removeItem("user")
            return fetch(apiPath("/auth/logout"), { method: "POST", credentials: "include" })
        }

        const onSessionReplacedEvent = () => {
            // Clear the httpOnly cookie via logout before redirecting.
            // This is critical: without it the stale cookie causes an infinite
            // SESSION_REPLACED loop on every page load.
            clearSession().finally(() => {
                onSessionReplacedRef.current?.()
            })
        }

        const onAdminIpDenied = () => {
            clearSession().catch(() => {})
        }

        window.addEventListener("session-replaced", onSessionReplacedEvent)
        window.addEventListener("admin-ip-denied", onAdminIpDenied)
        return () => {
            window.removeEventListener("session-replaced", onSessionReplacedEvent)
            window.removeEventListener("admin-ip-denied", onAdminIpDenied)
        }
    }, []) // empty deps — intentional, callback accessed via ref

    const handleLogout = useCallback(async () => {
        try { await api.post("/auth/logout") } catch (_) {}
        setUser(null)
        localStorage.removeItem("user")
        window.location.href = appPath("/")
    }, [])

    const setUserAndCache = useCallback((userData) => {
        setUser(userData)
        if (userData) localStorage.setItem("user", JSON.stringify(userData))
        else localStorage.removeItem("user")
    }, [])

    return { user, isAuthLoading, fetchUserData, handleLogout, setUserAndCache }
}
